import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Phone, Calendar, CreditCard, Check, AlertCircle } from 'lucide-react';

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
}

interface CustomerInfoFormProps {
  onSubmit: (customerInfo: CustomerInfo) => void;
  onBack: () => void;
  initialEmail?: string;
  isLoading?: boolean;
}

export const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  onSubmit,
  onBack,
  initialEmail,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    email: initialEmail || '',
    phone: '',
    birthDate: ''
  });

  const [errors, setErrors] = useState<Partial<CustomerInfo>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<CustomerInfo> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!/^[\+]?[\d\s\-\(\)]{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'Teléfono inválido';
    }

    if (!formData.birthDate) {
      newErrors.birthDate = 'La fecha de nacimiento es requerida';
    } else {
      const birthYear = new Date(formData.birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      if (currentYear - birthYear < 18 || currentYear - birthYear > 120) {
        newErrors.birthDate = 'Debe ser mayor de 18 años';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CustomerInfo, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Card className="w-full mx-auto max-w-md">
      <CardHeader className="text-center pb-4">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
          <User className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-lg sm:text-xl">Datos Personales</CardTitle>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Complete sus datos para proceder con el pago
        </p>
      </CardHeader>

      <CardContent className="px-4 sm:px-6">
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Nombre y Apellido en una fila en móvil */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium">
                Nombre *
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Su nombre"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-red-500' : ''}
                autoComplete="given-name"
              />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium">
                Apellido *
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Su apellido"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-red-500' : ''}
                autoComplete="family-name"
              />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium flex items-center">
              Email *
              <span className="ml-2 text-green-600">
                <Check className="h-3 w-3" />
              </span>
            </Label>
            <div className="relative group">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
              <Input
                id="email"
                type="email"
                inputMode="email"
                placeholder="correo@ejemplo.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`pl-10 transition-all duration-200 ${
                  errors.email 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                    : formData.email && !errors.email
                    ? 'border-green-500 focus:border-green-500 focus:ring-green-500/20'
                    : 'focus:border-primary focus:ring-primary/20'
                } group-focus-within:shadow-sm`}
                autoComplete="email"
              />
              {formData.email && !errors.email && (
                <div className="absolute right-3 top-3 text-green-600">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
            {errors.email && (
              <motion.p 
                className="text-xs text-red-500 flex items-center space-x-1"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <AlertCircle className="h-3 w-3" />
                <span>{errors.email}</span>
              </motion.p>
            )}
          </div>

          {/* Teléfono */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">
              Teléfono *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="+41 79 123 45 67"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                autoComplete="tel"
              />
            </div>
            {errors.phone && (
              <p className="text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Fecha de nacimiento */}
          <div className="space-y-2">
            <Label htmlFor="birthDate" className="text-sm font-medium">
              Fecha de Nacimiento *
            </Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={(e) => handleInputChange('birthDate', e.target.value)}
                className={`pl-10 ${errors.birthDate ? 'border-red-500' : ''}`}
                max={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
                autoComplete="bday"
              />
            </div>
            {errors.birthDate && (
              <p className="text-xs text-red-500">{errors.birthDate}</p>
            )}
          </div>

          {/* Aviso legal */}
          <div className="bg-muted/20 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Protección de datos:</strong> Sus datos personales serán utilizados únicamente para procesar su pedido y brindarle nuestros servicios. No compartimos su información con terceros.
            </p>
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isLoading}
              className="w-full sm:flex-1 order-2 sm:order-1"
            >
              Volver
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full sm:flex-1 bg-primary hover:bg-primary/90 order-1 sm:order-2"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Procesando...
                </div>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Proceder al Pago
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};